/**
 * Ad-hoc per-chat connector deselections for the Playground.
 *
 * The Playground's active server set mirrors the runtime set (a connector that
 * is connected is selected — `ActiveHostServerReconciler`), which made turning
 * one OFF for a chat impossible: the mirror re-added it on the next render
 * (2026-08-25). This store records the user's explicit "not in this chat"
 * intent; the mirror subtracts it, so a deselected connector stays out while
 * remaining connected (its tools return the moment it is toggled back on, no
 * reconnect). Session-scoped and per-chat: cleared when the previewed client
 * reseeds the selection, when a saved session is restored, and on a new chat —
 * the same points that reset the "user edited the selection" flag.
 */
import { useSyncExternalStore } from "react";

let deselected: ReadonlySet<string> = new Set();
const listeners = new Set<() => void>();

const notify = () => {
  for (const l of listeners) l();
};

export function getDeselectedConnectors(): ReadonlySet<string> {
  return deselected;
}

/** Record the user turning a connector off (or back on) for the current chat. */
export function setConnectorDeselected(name: string, off: boolean): void {
  if (off === deselected.has(name)) return;
  const next = new Set(deselected);
  if (off) next.add(name);
  else next.delete(name);
  deselected = next;
  notify();
}

export function clearDeselectedConnectors(): void {
  if (deselected.size === 0) return;
  deselected = new Set();
  notify();
}

export function subscribeDeselectedConnectors(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useDeselectedConnectors(): ReadonlySet<string> {
  return useSyncExternalStore(subscribeDeselectedConnectors, getDeselectedConnectors);
}
